import React from 'react'
import { Box, Button, Group, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'

import { CalendarCourtReporterAdminEvent } from '@/apiClient'
import { useModals } from '@/hooks/useModals'
import { useUnbookCourtReporterMutation } from '@/queries/courtReporters'
import { formatDuration, formatTimeRange } from '@/utils/time'

type Props = {
  event: CalendarCourtReporterAdminEvent
  onCancel: () => void
}

export const CancelCourtReporterBookingButton: React.FC<Props> = ({
  event,
  onCancel,
}) => {
  const modals = useModals()

  return (
    <Button
      color="red"
      variant="light"
      onClick={() =>
        modals.openModal({
          title: 'Do you want to cancel booking?',
          children: (
            <ConfirmCourtReporterCancelModal
              event={event}
              onCancel={onCancel}
            />
          ),
          centered: true,
        })
      }
    >
      Cancel booking
    </Button>
  )
}

const ConfirmCourtReporterCancelModal: React.FC<Props> = ({
  event,
  onCancel,
}) => {
  const modals = useModals()

  const { mutateAsync: unbook, isLoading } = useUnbookCourtReporterMutation(
    event,
    {
      onSuccess: () => {
        // we don't use confirm modal here so we have to close the modal manually
        modals.closeAll()
        showNotification({
          message: <Text weight={600}>Court reporter booking cancelled</Text>,
        })
        onCancel()
      },
      onErrorMessage: message => {
        modals.closeAll()
        showNotification({ color: 'red', message })
      },
    },
  )

  return (
    <>
      <Box mb={20}>
        Do you want to cancel the court reporter booking for {event.name} on{' '}
        {formatTimeRange(event.datetime, event.duration, { long: true })} (
        {formatDuration(event.duration)})?
      </Box>
      <Group position="right">
        <Button variant="light" color="gray" onClick={() => modals.closeAll()}>
          Close
        </Button>
        <Button
          color="red"
          variant="filled"
          loading={isLoading}
          onClick={() => unbook()}
        >
          Cancel booking
        </Button>
      </Group>
    </>
  )
}
